import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, Link as LinkIcon, MapPin } from 'lucide-react'
import {
  PiIdentificationCard,
  PiHandshake,
  PiNotepad,
  PiFileText,
  PiCalendarStar,
} from 'react-icons/pi'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { MobileColors } from 'styles/mobileTokens'
import { formatInteger, formatDate2 } from 'utils/formatting'
import { MobileAccountAttributesSummary } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileDealsAccordion } from 'components/MobileCompanyPage/MobileDealsAccordion'
import { MobileEventHistoryAccordion } from 'components/MobileCompanyPage/MobileEventHistory'
import { MobileRemindersAccordion } from 'components/MobileCompanyPage/MobileReminders'
import useDocumentTitle from 'components/useDocumentTitle'
import { FirstBiteDataDrawer } from 'components/MobileCompanyPage/FirstBiteDataDrawer'
import {
  DescriptionAndContactInfo,
  Divider,
  MetricCard,
  StatsCard,
  AdditionalCharacteristics,
  Rating,
} from 'components/NewCompanyPage/Sidebar/shared'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { BackButton } from 'components/Mobile/Mobile.styles'

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${MobileColors.backgroundWhite};
  overflow-y: auto;
  padding-bottom: 200px; /* Space for drawer handle */
`

const CompanyHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const CompanyHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const CompanyLogoContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid ${MobileColors.borderDefault};
`

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const EmptyLogo = styled.div`
  width: 100%;
  height: 100%;
  background-color: #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CompanyName = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 0;
  flex: 1;
`

const CompanyMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const MetaDivider = styled.span`
  color: ${MobileColors.borderDefault};
`

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${MobileColors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

// Chain-style buttons row
const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  padding: 12px 10px;
  background: ${MobileColors.primary};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px;
  background: ${MobileColors.primaryActive};
  border: none;
  border-radius: 8px;
  margin: 3px;
  color: #ffffff;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 20px;
    color: #ffffff;
  }

  &:hover {
    background: ${MobileColors.primary};
  }

  &:active {
    background: ${MobileColors.primaryDark};
  }
`

const ActionCount = styled.span`
  font-size: 11px;
  font-weight: 300;
`

const ContentSection = styled.div`
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
`

const SectionDivider = styled.div`
  height: 8px;
  background: ${MobileColors.backgroundOverlay};
`

const ChainLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${MobileColors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export function MobileDoorPage() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const doorId = urlParams?.door_id

  const api = useApiService()
  const { data: doorData, isLoading } = api.useGetPlaceV2(doorId)

  // Get door proxy data for counts
  const { data: doorProxyData } = api.useGetDoorProxy(
    doorData?.id ? doorData.id : undefined
  )

  // Set document title
  useDocumentTitle(
    `${doorData?.restaurant?.restaurant_name || 'Not Found'} (Door) - First Bite`
  )

  // Get modified date from chain_proxy
  const modifiedDate = doorData?.chain_proxy?.modified

  // Helper to get chain page URL
  const getChainUrl = () => {
    if (!doorData?.chain_proxy?.chain?.id) return null
    const prefix =
      preferences?.company_type_prefix_map[ContactCompanyType.Chain] || ''
    return `/chain/${prefix}${doorData.chain_proxy.chain.id}`
  }

  // Build full address
  const getFullAddress = () => {
    const parts = [
      doorData?.restaurant?.address,
      doorData?.restaurant?.city,
      doorData?.restaurant?.state,
    ].filter(Boolean)
    return parts.join(', ')
  }

  const renderPredictedTraffic = (group: string) => {
    const badges = group.split(',').map((traffic) => traffic.trim())
    return (
      <div className="flex gap-1">
        {badges.map((badge, index) => (
          <PredictedTrafficBadge key={index} value={badge} size="small" />
        ))}
      </div>
    )
  }

  // Build stats for summary tab
  const stats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cuisine',
      value: doorData?.restaurant?.cuisine_50 || '-',
    },
    {
      label: 'Predicted Traffic',
      value: doorData?.restaurant?.velocity_group
        ? renderPredictedTraffic(doorData.restaurant.velocity_group)
        : '-',
    },
    {
      label: 'Expense Category',
      value: doorData?.restaurant?.expense_category ? (
        <span className="font-medium text-green-700">
          {doorData.restaurant.expense_category}
        </span>
      ) : (
        '-'
      ),
    },
    {
      label: 'Average Rating',
      value: doorData?.restaurant?.avg_rating ? (
        <Rating value={doorData.restaurant.avg_rating} />
      ) : (
        '-'
      ),
    },
    {
      label: 'Total Reviews',
      value: doorData?.restaurant?.total_reviews_count
        ? formatInteger(doorData.restaurant.total_reviews_count)
        : '0',
    },
  ]

  // Build characteristics
  const characteristics = [
    ...(doorData?.restaurant?.service?.split(',') || []),
    ...(doorData?.restaurant?.atmosphere?.split(',') || []),
    ...(doorData?.restaurant?.offerings?.split(',') || []),
    ...(doorData?.restaurant?.meals?.split(',') || []),
  ]
    .map((c) => c?.trim())
    .filter(Boolean)

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = [
    <DescriptionAndContactInfo
      key="description-contact"
      description={doorData?.restaurant?.description}
    />,
    <div key="metrics" className="grid grid-cols-2 gap-3">
      <MetricCard
        key="menu-size"
        value={formatInteger(doorData?.restaurant?.menu_size || 0)}
        label="Menu Size"
        actionText="View Menu"
        onActionClick={() => toast.info('View Menu - Coming soon')}
      />
      {doorData?.chain_proxy?.chain?.id && (
        <MetricCard
          key="chain-size"
          value={formatInteger(doorData?.chain_proxy?.doors_count || 0)}
          label="Chain Size"
          actionText="View Chain"
          onActionClick={() => {
            const chainUrl = getChainUrl()
            if (chainUrl) navigate(chainUrl)
          }}
        />
      )}
    </div>,
    <StatsCard key="stats" stats={stats} />,
    <Divider key="divider-3" />,
    <AdditionalCharacteristics
      key="characteristics"
      characteristics={characteristics}
    />,
  ]

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = [
    <MetricCard
      key="address"
      value={getFullAddress() || 'N/A'}
      label="Address"
      variant="full-width"
    />,
    <Divider key="divider-2" />,
    <div key="location-info" className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">City</div>
          <div className="text-sm font-medium">
            {doorData?.restaurant?.city || 'N/A'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">State</div>
          <div className="text-sm font-medium">
            {doorData?.restaurant?.state || 'N/A'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Country</div>
          <div className="text-sm font-medium">
            {doorData?.restaurant?.country || 'N/A'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Phone</div>
          <div className="text-sm font-medium">
            {doorData?.restaurant?.phone || 'N/A'}
          </div>
        </div>
      </div>
    </div>,
  ]

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!doorData) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Door not found</p>
        </div>
      </PageContainer>
    )
  }

  const chainProxyId = doorData.chain_proxy?.id
  const chainUrl = getChainUrl()
  // slug for /chain/:chain_name/account-attributes
  const chainRouteParam = chainUrl?.replace('/chain/', '') || ''

  return (
    <>
      <PageContainer>
        {/* Back Button */}
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>

        {/* Company Header */}
        <CompanyHeaderSection>
          {/* Logo and Name Row */}
          <CompanyHeaderRow>
            <CompanyLogoContainer>
              {doorData.restaurant?.logo_url ? (
                <CompanyLogo
                  src={doorData.restaurant.logo_url}
                  alt={doorData.restaurant?.restaurant_name || ''}
                />
              ) : (
                <EmptyLogo />
              )}
            </CompanyLogoContainer>
            <CompanyName>{doorData.restaurant?.restaurant_name}</CompanyName>
          </CompanyHeaderRow>

          {/* Meta Row: Address, Updated Date, Social Icons */}
          <CompanyMetaRow>
            {getFullAddress() && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {getFullAddress()}
              </span>
            )}
            {modifiedDate && (
              <>
                <MetaDivider>•</MetaDivider>
                <span>Updated {formatDate2(modifiedDate)}</span>
              </>
            )}
            {doorData.restaurant?.domain && (
              <>
                <MetaDivider>•</MetaDivider>
                <SocialLink
                  href={
                    doorData.restaurant.domain.startsWith('http')
                      ? doorData.restaurant.domain
                      : `https://${doorData.restaurant.domain}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit website"
                >
                  <LinkIcon />
                </SocialLink>
              </>
            )}
          </CompanyMetaRow>

          {/* Chain Link */}
          {chainUrl && doorData.chain_proxy?.chain && (
            <ChainLinkContainer onClick={() => navigate(chainUrl)}>
              <span>Part of {doorData.chain_proxy.chain.chain}</span>
              <AFbLink className="!text-xs">View Chain →</AFbLink>
            </ChainLinkContainer>
          )}
        </CompanyHeaderSection>

        {/* Action Buttons (5 buttons, chain-style layout) */}
        <ActionButtonsRow>
          <ActionButton
            onClick={() => navigate(`/deals?door_proxy_id=${doorData.id}`)}
          >
            <PiHandshake />
            <ActionCount>({doorProxyData?.deal_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() => navigate(`/contacts?door_proxy_id=${doorData.id}`)}
          >
            <PiIdentificationCard />
            <ActionCount>({doorProxyData?.contact_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton onClick={() => toast.info('Samples - Coming soon')}>
            <PiFileText />
            <ActionCount>(0)</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() =>
              navigate(
                `/notes?door_proxy_id=${doorData.id}&door_name=${encodeURIComponent(
                  doorData.restaurant?.restaurant_name || ''
                )}&contact_company_id=${chainProxyId || ''}&company_name=${encodeURIComponent(
                  doorData.chain_proxy?.chain?.chain || ''
                )}`
              )
            }
          >
            <PiNotepad />
            <ActionCount>({doorProxyData?.note_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() =>
              navigate(
                `/reminders?door_id=${doorData.id}&door_name=${encodeURIComponent(
                  doorData.restaurant?.restaurant_name || ''
                )}`
              )
            }
          >
            <PiCalendarStar />
            <ActionCount>(0)</ActionCount>
          </ActionButton>
        </ActionButtonsRow>

        {/* Account Attributes Section */}
        {chainProxyId && (
          <ContentSection>
            <MobileAccountAttributesSummary
              chainProxyId={chainProxyId}
              chainName={chainRouteParam}
              doorProxyId={doorData.id}
            />
          </ContentSection>
        )}

        <SectionDivider />

        {/* Deals Overview Section */}
        {chainProxyId && (
          <ContentSection>
            <MobileDealsAccordion
              chainProxyId={chainProxyId}
              doorProxyId={doorData.id}
            />
          </ContentSection>
        )}

        <SectionDivider />

        {/* Reminders Section */}
        {chainProxyId && (
          <ContentSection>
            <MobileRemindersAccordion
              chainProxyId={chainProxyId}
              doorProxyId={doorData.id}
            />
          </ContentSection>
        )}

        <SectionDivider />

        {/* Event History Section */}
        {chainProxyId && (
          <ContentSection>
            <MobileEventHistoryAccordion
              chainProxyId={chainProxyId}
              doorProxyId={doorData.id}
            />
          </ContentSection>
        )}
      </PageContainer>

      {/* First Bite Data Drawer */}
      {chainProxyId && (
        <FirstBiteDataDrawer
          chainID={doorData.chain_proxy?.chain?.id}
          chainProxyId={chainProxyId}
          doorProxyId={doorData.id}
          doorName={doorData.restaurant?.restaurant_name}
          doorAddress={getFullAddress()}
          chainName={doorData.restaurant?.restaurant_name}
          logo={doorData.restaurant?.logo_url}
          summaryContent={summaryTabContent}
          locationDetailsContent={locationDetailsTabContent}
        />
      )}
    </>
  )
}
